import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Save, 
  LogOut, 
  DollarSign, 
  BarChart3, 
  Type, 
  Image,
  Eye,
  Check,
  AlertCircle,
  Settings,
  Home
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WebsiteAdmin = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('pricing');
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');

  // Content state
  const [pricing, setPricing] = useState([
    {
      id: 'freemium',
      name: 'Freemium',
      price: '0',
      period: '/mois',
      description: 'Pour découvrir SyndicAppli',
      features: ['1 immeuble', '2 AG par an', 'Messagerie basique', 'Support email'],
      popular: false
    },
    {
      id: 'premium',
      name: 'Premium',
      price: '49',
      period: '/mois',
      description: 'Pour les petits cabinets',
      features: ['Jusqu\'à 10 immeubles', 'AG illimitées', 'Visioconférence incluse', 'Comptabilité complète', 'Support prioritaire'],
      popular: true
    },
    {
      id: 'unlimited',
      name: 'Illimité',
      price: '99',
      period: '/mois',
      description: 'Pour les cabinets en croissance',
      features: ['Immeubles illimités', 'Toutes les fonctionnalités', 'API et intégrations', 'Formation personnalisée', 'Account manager dédié'],
      popular: false
    }
  ]);

  const [stats, setStats] = useState([
    { id: 'cafes', label: 'Cafés bus', value: 7530 },
    { id: 'months', label: 'Mois de développement', value: 48 },
    { id: 'france', label: 'Made in France', value: 100, suffix: '%' },
    { id: 'users', label: 'Utilisateurs actifs', value: 500, suffix: '+' }
  ]);

  const [texts, setTexts] = useState({
    heroTitle: "L'application qui gère vos AG de copropriété",
    heroSubtitle: "Simplifiez votre quotidien de syndic avec notre solution tout-en-un.",
    featuresTitle: "Tout ce dont vous avez besoin",
    featuresSubtitle: "Une suite complète d'outils pour gérer efficacement vos copropriétés au quotidien.",
    ctaTitle: "Prêt à simplifier votre gestion de copropriété ?",
    ctaSubtitle: "Rejoignez plus de 500 syndics qui nous font déjà confiance"
  });

  // Load data from API
  useEffect(() => {
    if (isAuthenticated) {
      loadContent();
    }
  }, [isAuthenticated]);

  const loadContent = async () => {
    try {
      const token = localStorage.getItem('website_admin_token');
      const response = await fetch(`${API_URL}/api/website-content`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.pricing) setPricing(data.pricing);
        if (data.stats) setStats(data.stats);
        if (data.texts) setTexts(data.texts);
      }
    } catch (error) {
      console.error('Error loading content:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData)
      });

      if (response.ok) {
        const data = await response.json();
        if (data.user?.role === 'admin' || data.user?.role === 'gestionnaire') {
          localStorage.setItem('website_admin_token', data.token);
          setIsAuthenticated(true);
        } else {
          setLoginError('Accès non autorisé. Compte administrateur requis.');
        }
      } else {
        setLoginError('Email ou mot de passe incorrect.');
      }
    } catch (error) {
      setLoginError('Erreur de connexion. Veuillez réessayer.');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('website_admin_token');
    setIsAuthenticated(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveStatus(null);

    try {
      const token = localStorage.getItem('website_admin_token');
      const response = await fetch(`${API_URL}/api/website-content`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ pricing, stats, texts })
      });

      if (response.ok) {
        setSaveStatus('success');
      } else {
        setSaveStatus('error');
      }
    } catch (error) {
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  const updatePricing = (index, field, value) => {
    const updated = [...pricing];
    if (field === 'features') {
      updated[index].features = value.split('\n');
    } else {
      updated[index][field] = value;
    }
    setPricing(updated);
  };

  const updateStat = (index, field, value) => {
    const updated = [...stats];
    updated[index][field] = field === 'value' ? parseInt(value) || 0 : value;
    setStats(updated);
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700 flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <Settings className="w-8 h-8 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Administration Site</h1>
            <p className="text-slate-500 mt-2">Connectez-vous pour gérer le contenu</p>
          </div>

          {loginError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm flex items-center gap-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              {loginError}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
              <input
                type="email"
                required
                value={loginData.email}
                onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none"
                placeholder="admin@syndicappli.fr"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Mot de passe</label>
              <input
                type="password"
                required
                value={loginData.password}
                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit"
              className="w-full py-3 bg-emerald-500 text-white rounded-xl font-semibold hover:bg-emerald-600 transition-colors"
            >
              Se connecter
            </button>
          </form>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'pricing', label: 'Tarifs', icon: DollarSign },
    { id: 'stats', label: 'Statistiques', icon: BarChart3 },
    { id: 'texts', label: 'Textes', icon: Type },
  ];

  return (
    <div className="min-h-screen bg-slate-100" data-testid="website-admin">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center">
              <Settings className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-slate-900">Administration Site</h1>
              <p className="text-sm text-slate-500">Gérez le contenu du site vitrine</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <a
              href="/site"
              target="_blank"
              className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <Eye className="w-4 h-4" />
              <span className="hidden sm:inline">Voir le site</span>
            </a>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-2 px-5 py-2 bg-emerald-500 text-white rounded-lg font-medium hover:bg-emerald-600 transition-colors disabled:opacity-50"
            >
              {isSaving ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : saveStatus === 'success' ? (
                <Check className="w-4 h-4" />
              ) : saveStatus === 'error' ? (
                <AlertCircle className="w-4 h-4" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">
                {isSaving ? 'Enregistrement...' : saveStatus === 'success' ? 'Enregistré !' : 'Enregistrer'}
              </span>
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-3 rounded-xl font-medium whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30'
                  : 'bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-sm p-8">
          {/* Pricing Tab */}
          {activeTab === 'pricing' && (
            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-6">Gestion des tarifs</h2>
              <div className="grid md:grid-cols-3 gap-6">
                {pricing.map((plan, index) => (
                  <div key={plan.id} className="border border-slate-200 rounded-xl p-6">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Nom de l'offre</label>
                        <input
                          type="text"
                          value={plan.name}
                          onChange={(e) => updatePricing(index, 'name', e.target.value)}
                          className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Prix (€)</label>
                        <input
                          type="text"
                          value={plan.price}
                          onChange={(e) => updatePricing(index, 'price', e.target.value)}
                          className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                        <input
                          type="text"
                          value={plan.description}
                          onChange={(e) => updatePricing(index, 'description', e.target.value)}
                          className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Fonctionnalités (une par ligne)
                        </label>
                        <textarea
                          value={plan.features.join('\n')}
                          onChange={(e) => updatePricing(index, 'features', e.target.value)}
                          rows={5}
                          className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none resize-none"
                        />
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={plan.popular}
                          onChange={(e) => updatePricing(index, 'popular', e.target.checked)}
                          className="w-4 h-4 rounded text-emerald-500 focus:ring-emerald-500"
                        />
                        <span className="text-sm text-slate-600">Offre populaire (badge)</span>
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Stats Tab */}
          {activeTab === 'stats' && (
            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-6">Statistiques affichées</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {stats.map((stat, index) => (
                  <div key={stat.id} className="border border-slate-200 rounded-xl p-6">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Label</label>
                        <input
                          type="text"
                          value={stat.label}
                          onChange={(e) => updateStat(index, 'label', e.target.value)}
                          className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Valeur</label>
                        <input
                          type="number"
                          value={stat.value}
                          onChange={(e) => updateStat(index, 'value', e.target.value)}
                          className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Suffixe (optionnel)</label>
                        <input
                          type="text"
                          value={stat.suffix || ''}
                          onChange={(e) => updateStat(index, 'suffix', e.target.value)}
                          placeholder="ex: %, +, €"
                          className="w-full px-3 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Texts Tab */}
          {activeTab === 'texts' && (
            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-6">Textes du site</h2>
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Titre Hero</label>
                    <input
                      type="text"
                      value={texts.heroTitle}
                      onChange={(e) => setTexts({ ...texts, heroTitle: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Sous-titre Hero</label>
                    <input
                      type="text"
                      value={texts.heroSubtitle}
                      onChange={(e) => setTexts({ ...texts, heroSubtitle: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Titre section Fonctionnalités</label>
                    <input
                      type="text"
                      value={texts.featuresTitle}
                      onChange={(e) => setTexts({ ...texts, featuresTitle: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Sous-titre Fonctionnalités</label>
                    <input
                      type="text"
                      value={texts.featuresSubtitle}
                      onChange={(e) => setTexts({ ...texts, featuresSubtitle: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Titre CTA final</label>
                    <input
                      type="text"
                      value={texts.ctaTitle}
                      onChange={(e) => setTexts({ ...texts, ctaTitle: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Sous-titre CTA</label>
                    <input
                      type="text"
                      value={texts.ctaSubtitle}
                      onChange={(e) => setTexts({ ...texts, ctaSubtitle: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export { WebsiteAdmin };
